import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, LayoutGrid, LayoutList, MapPin, Building2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { venueUrl } from '@/lib/urls';
import ConsumerLayout from '@/components/ConsumerLayout';
import { VenueGuideCard } from '@/components/GuideCard';
import { Link } from 'react-router-dom';
import { useLocation } from '@/contexts/LocationContext';
import api from '@/lib/api';

const VenueBrowse = () => {
  const { currentCity } = useLocation();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('name');
  const [reviewStats, setReviewStats] = useState({});

  useEffect(() => {
    fetchVenues();
  }, [currentCity]);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const cityParam = currentCity?.city ? `?city=${currentCity.city}` : '';
      const response = await api.get(`/discover/venues${cityParam}`);
      const venueList = response.data.venues || response.data || [];
      setVenues(venueList);
      
      // Fetch review stats for each venue
      const stats = {};
      for (const venue of venueList) {
        try {
          const reviewsRes = await api.get('/reviews', {
            params: { target_type: 'venue', target_id: venue.id }
          });
          const reviews = reviewsRes.data;
          if (reviews.length > 0) {
            const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
            stats[venue.id] = { avgRating, count: reviews.length };
          }
        } catch (e) {
          // Skip if reviews fail
        }
      }
      setReviewStats(stats);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVenues = venues
    .filter(venue =>
      venue.name.toLowerCase().includes(search.toLowerCase()) ||
      venue.description.toLowerCase().includes(search.toLowerCase()) ||
      venue.district.toLowerCase().includes(search.toLowerCase()) ||
      venue.address.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'rating') {
        const ratingA = reviewStats[a.id]?.avgRating || 0;
        const ratingB = reviewStats[b.id]?.avgRating || 0;
        return ratingB - ratingA;
      }
      return 0;
    });

  const CompactVenueCard = ({ venue, index }) => {
    const stats = reviewStats[venue.id];
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        whileHover={{ y: -4 }}
        data-testid={`compact-venue-card-${venue.id}`}
      >
        <Link to={venueUrl(venue)}>
          <Card className="overflow-hidden border border-border/50 hover:border-indigo-500/50 transition-all cursor-pointer group h-full">
            <div className="aspect-[4/3] bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 relative overflow-hidden">
              {venue.images && venue.images[0] ? (
                <img
                  src={venue.images[0]}
                  alt={venue.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Building2 className="w-12 h-12 text-indigo-400" />
                </div>
              )}
              <Badge className="absolute top-3 right-3 bg-yellow-400/90 text-black font-bold border-0">
                {venue.district}
              </Badge>
              {stats && (
                <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-full flex items-center gap-1 text-sm">
                  <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                  <span className="font-bold">{stats.avgRating.toFixed(1)}</span>
                </div>
              )}
            </div>
            <CardContent className="p-4">
              <h3 className="font-bold text-foreground mb-2 line-clamp-1 group-hover:text-indigo-600 transition-colors">
                {venue.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {venue.description}
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{venue.address}</span>
              </div>
            </CardContent>
          </Card>
        </Link>
      </motion.div>
    );
  };

  return (
    <ConsumerLayout>

      <section className="relative py-16 lg:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1642721085217-b5f5b6b47d0a?crop=entropy&cs=srgb&fm=jpg&q=85')] bg-cover bg-center" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-8 h-8 text-yellow-400" />
              <Badge variant="secondary" className="bg-white/20 text-white border-0">
                {filteredVenues.length} Partner Venues
              </Badge>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="venues-page-title">
              RiNo Partner Venues
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Discover the bars, restaurants, galleries, and event spaces that make Denver's Art District 
              the cultural heartbeat of the city. Each venue is hand-picked for exceptional experiences.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search venues by name, location..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20"
                  data-testid="venue-search-input"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-foreground">
              Browse All Venues
            </h2>
            <p className="text-sm text-muted-foreground">
              Explore events, experiences, and private hire at each location
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40" data-testid="venue-sort-select">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name A-Z</SelectItem>
                <SelectItem value="rating">Top Rated</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('list')}
                className="rounded-r-none"
                data-testid="venue-view-mode-list"
              >
                <LayoutList className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('grid')}
                className="rounded-l-none"
                data-testid="venue-view-mode-grid"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
          </div>
        ) : filteredVenues.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">No venues found</h3>
            <p className="text-muted-foreground mb-6">
              {search ? 'Try adjusting your search terms' : 'Check back soon for new partner venues'}
            </p>
            {search && (
              <Button variant="outline" onClick={() => setSearch('')}>
                Clear Search
              </Button>
            )}
          </motion.div>
        ) : viewMode === 'list' ? (
          <div className="space-y-6">
            {filteredVenues.map((venue, index) => (
              <VenueGuideCard 
                key={venue.id} 
                venue={venue} 
                index={index} 
                reviewStats={reviewStats[venue.id]}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredVenues.map((venue, index) => (
              <CompactVenueCard key={venue.id} venue={venue} index={index} />
            ))}
          </div>
        )}

        {filteredVenues.length > 0 && (
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Showing {filteredVenues.length} partner venues in RiNo Art District
            </p>
          </div>
        )}
      </div>
    </ConsumerLayout>

  );
};

export default VenueBrowse;
